import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { getImageUrl } from '../../utils/assets';
import { getCelebrationInfo, CelebrationTab } from '../../api/celebration';
import Loading from './Loading';
import './CelebrationModal.css';

interface CelebrationModalProps {
  visible: boolean;
  onClose: () => void;
}

const CelebrationModal: React.FC<CelebrationModalProps> = ({ visible, onClose }) => {
  const { t } = useTranslation(['game']);
  const [activeTabId, setActiveTabId] = useState<string>('announcement');
  const [tabs, setTabs] = useState<CelebrationTab[]>([]);
  const [loading, setLoading] = useState(false);

  // Helper to get image path
  const getImg = (name: string) => `url(${getImageUrl('gameplay/' + name)})`;

  useEffect(() => {
    if (visible) {
      fetchCelebrationInfo();
    }
  }, [visible]);

  const fetchCelebrationInfo = async () => {
    setLoading(true);
    try {
      const data = await getCelebrationInfo();
      if (data && data.length > 0) {
        setTabs(data);
        // Set first tab as active if current active is not in list
        if (!data.find(tab => tab.id === activeTabId)) {
          setActiveTabId(data[0].id);
        }
      }
    } catch (error) {
      console.error('Failed to fetch celebration info', error);
      toast.error(t('common.loadingError', 'Failed to load content'));
    } finally {
      setLoading(false);
    }
  };

  const activeTab = tabs.find(t => t.id === activeTabId) || tabs[0];

  if (!visible) return null;

  return (
    <div className={`celebration-popup-container ${visible ? 'visible' : ''}`}>
      {/* Backdrop */}
      <div 
        className="celebration-popup-backdrop" 
        style={{ backgroundImage: getImg('popup-backdrop.png') }} 
        onClick={onClose} 
      />

      <div className="celebration-content-wrapper">
        <div className="qd-main-frame">
          {/* Background Overlay */}
          <div 
            className="qd-background-overlay" 
          />

          {/* Content Panel */}
          <div 
            className="qd-content-panel" 
            style={{ backgroundImage: getImg('qd_content_panel.png') }} 
          />

          {/* Close Button */}
          <div 
            className="qd-close-button" 
            style={{ backgroundImage: getImg('qd_close_button.png') }} 
            onClick={onClose}
          />

          {/* Image Container (Frame) */}
          <div 
            className="qd-image-container" 
            style={{ backgroundImage: getImg('qd_image_container.png') }} 
          />

          {/* Title */}
          <span className="qd-title-text">{t('celebration.title')}</span>

          {loading && <Loading text={t('common.loading')} />}

          {!loading && (
            <>
              {/* Tabs */}
              {tabs.map((tab, index) => {
                const isActive = activeTabId === tab.id;
                // Calculate position based on index to avoid overlapping if IDs don't match CSS
                // Start at 1.14rem, step is roughly 1.31rem (based on CSS: 1.14, 2.45, 3.76, 5.07)
                const leftPos = 1.14 + (index * 1.31);
                
                return (
                  <div 
                    key={tab.id}
                    className="qd-tab" 
                    style={{ 
                      backgroundImage: isActive ? getImg('qd_tab_active.png') : getImg('qd_tab_inactive.png'),
                      left: `${leftPos}rem`
                    }}
                    onClick={() => setActiveTabId(tab.id)}
                  >
                    <span className={`qd-tab-text ${isActive ? 'active' : 'inactive'}`}>
                      {tab.label}
                    </span>
                  </div>
                );
              })}

              {/* Scrollable Content Area (Image + Text) */}
              <div className="qd-scroll-container">
                {/* Banner Image acting as background container for text */}
                <div 
                  className="qd-banner-image" 
                  style={{ backgroundImage: getImg('qd_image_container.png') }} 
                >
                  {/* Text Content */}
                  <div 
                    className="qd-text-content"
                    dangerouslySetInnerHTML={{ __html: activeTab?.content || '' }}
                  />
                </div>
              </div>
            </>
          )}

          {/* Confirm Button */}
          <div 
            className="qd-confirm-button" 
            style={{ backgroundImage: getImg('qd_button_primary.png') }}
            onClick={onClose}
          />
          <span className="qd-confirm-text">{t('celebration.confirm')}</span>

        </div>
      </div>
    </div>
  );
};

export default CelebrationModal;
